'use memo';

import {
  AgxButton,
  AgxModal,
  AgxSpacer,
  AgxTypography,
  Breakpoints,
  Icon_AlertCircleOutline,
  Icon_SparkleForm,
  useWindowSize,
} from '@urbanx/agx.ui-components2';
import {
  EnvelopeSubmissionType,
  FormConfigType,
  MarketingPackageStage,
  SPECIAL_CHARACTERS,
} from '@urbanx/agx-ui-components';
import { completeMarketingPackageStep } from 'Api/Marketing/marketingApi';
import { AuthContext } from '@urbanx/agx.auth.client';
import { useFormLoader } from 'hooks/useFormLoader';
import { useContext, useState } from 'react';
import { setAndShowErrorToast } from 'store/config';
import { formatCurrency } from 'utils/formatNumber';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { getMarketingPackageTotal } from 'utils/marketingPackageUtil';
import { CampaignDetailModel } from '@urbanx/agx.campaigns.client';
import { MarketingPackageProjection } from '@urbanx/agx.marketing.client';

interface Props {
  onBack: () => void;
  campaign: CampaignDetailModel;
  marketingPackage: MarketingPackageProjection;
  addressLineOne: string;
  addressLineTwo: string;
}

const ResumeMarketingPackage = (props: Props) => {
  const { campaign, marketingPackage, addressLineOne, addressLineTwo } = props;
  const { campaignId } = campaign;

  const dispatch = useAppDispatch();
  const { getAuthToken } = useContext(AuthContext);
  const { loadForm, loadFormWithConfig } = useFormLoader();

  const [showConfirmationModel, setShowConfirmationModel] = useState(false);
  const [disableButtons, setDisableButtons] = useState(false);
  const screenSize = useWindowSize();
  const isMobile = screenSize === Breakpoints.Mobile;

  const marketingPackageTotal = getMarketingPackageTotal(marketingPackage);

  const resumeMarketingPackage = async () => {
    try {
      setDisableButtons(true);

      await loadForm(
        campaignId ?? '',
        marketingPackage.id ?? '',
        FormConfigType.Marketing
      );
    } catch (err) {
      console.error(err);
      dispatch(setAndShowErrorToast('Failed to resume Marketing'));
    }

    setDisableButtons(false);
  };

  const beginMarketingPackageSubmission = async () => {
    try {
      setDisableButtons(true);

      const authToken = await getAuthToken();

      if (!authToken) {
        dispatch(setAndShowErrorToast('Failed to start Marketing submission'));
        setDisableButtons(false);
        return;
      }

      const newForm = await completeMarketingPackageStep(
        authToken,
        campaignId ?? '',
        marketingPackage.id ?? '',
        true
      );

      if (!newForm) {
        dispatch(setAndShowErrorToast('Failed to start Marketing submission'));
        setDisableButtons(false);
        return;
      }

      await loadFormWithConfig(newForm);
    } catch (err) {
      console.error(err);
      dispatch(setAndShowErrorToast('Failed to start Marketing submission'));
    }

    setDisableButtons(false);
  };

  const confirmationDialogBody = (
    <AgxSpacer
      direction="vertical"
      gap="spacing-xl"
      className="confirmationModalStyle"
    >
      <AgxTypography
        id="marketing-package-start-submission-warning"
        type="body"
        size="medium"
      >
        You will not be able to edit the Marketing Package once you continue.
      </AgxTypography>
      <AgxTypography
        id="marketing-package-start-submission-question"
        type="body"
        size="medium"
      >
        Continue to submission?
      </AgxTypography>
    </AgxSpacer>
  );

  const confirmationDialogActions = (
    <AgxSpacer
      direction="horizontal"
      gap="md"
      contentJustification="center"
      fill
      className="confirmationButtonContainerStyle"
    >
      <AgxButton
        id="startsubmission"
        size="large"
        variant="primary"
        text="Yes, start submission"
        onClick={beginMarketingPackageSubmission}
        disabled={disableButtons}
        stretch
      />
      <AgxButton
        id="cancel"
        variant="hollow"
        size="large"
        text="No, go back"
        onClick={() => setShowConfirmationModel(false)}
        disabled={disableButtons}
        stretch
      />
    </AgxSpacer>
  );

  return (
    <>
      <AgxSpacer
        direction="vertical"
        gap="spacing-xl"
        contentAlignment="center"
      >
        <AgxTypography
          id="resume-marketing-package-address"
          type="heading"
          size="2x-large"
          style={{ textAlign: 'center' }}
        >
          {addressLineOne},
          <br />
          {addressLineTwo}
        </AgxTypography>
        <AgxTypography
          id="resume-marketing-package-name-total"
          type="body"
          size="medium"
          style={{ textAlign: 'center' }}
        >
          {marketingPackage.packageTemplateName} {SPECIAL_CHARACTERS.BULLET}{' '}
          {formatCurrency(marketingPackageTotal, 0)}
        </AgxTypography>
        <AgxSpacer direction="vertical" gap="md" contentAlignment="center">
          {!marketingPackage.document && (
            <AgxSpacer direction="vertical" gap="xs" contentAlignment="center">
              <AgxTypography
                id="resume-marketing-package-more-fields"
                type="body"
                size="medium"
                style={{ textAlign: 'center' }}
              >
                There are more fields to fill,
              </AgxTypography>
              <AgxTypography
                id="resume-marketing-package-continue"
                type="body"
                size="medium"
                style={{ textAlign: 'center' }}
              >
                let's continue from where you left off
              </AgxTypography>
            </AgxSpacer>
          )}
        </AgxSpacer>
        <Icon_SparkleForm
          width={isMobile ? 150 : 200}
          height={isMobile ? 150 : 200}
        />
      </AgxSpacer>
      <AgxSpacer
        fill
        direction="vertical"
        gap="md"
        contentAlignment="center"
        contentJustification="center"
      >
        <AgxButton
          id="btn-resume"
          variant={'hollow'}
          stretch
          size="large"
          text={`Resume ${
            marketingPackage.stage?.value ===
            MarketingPackageStage.SubmissionIncomplete
              ? 'Submission'
              : 'Marketing'
          }`}
          onClick={resumeMarketingPackage}
          disabled={disableButtons}
        />
        {marketingPackage.envelopeSubmission?.type !==
          EnvelopeSubmissionType.Manual &&
          marketingPackage.document && (
            <AgxButton
              id="btn-startSubmission"
              variant="primary"
              stretch
              size="large"
              disabled={disableButtons}
              text="Start Submission"
              onClick={() => setShowConfirmationModel(true)}
            />
          )}
      </AgxSpacer>
      {showConfirmationModel && (
        <AgxModal
          id="resume-marketing-start-submission-modal"
          title="Starting a submission?"
          titleIcon={<Icon_AlertCircleOutline type="Warning" />}
          content={confirmationDialogBody}
          actionButtons={confirmationDialogActions}
          actionButtonContainerStyle="withoutDivider"
          type="single"
          variant="withoutDivider"
          setIsDialogOpen={setShowConfirmationModel}
        />
      )}
    </>
  );
};

export default ResumeMarketingPackage;
